import { BigNumber } from "@ethersproject/bignumber"
import { Token } from "../constants"
import { Erc20 } from "../../types/ethers-contracts/Erc20"
import { LpTokenUnguarded } from "../../types/ethers-contracts/LpTokenUnguarded"
import { MaxUint256 } from "@ethersproject/constants"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { useCallback } from "react"

interface ApproveParam {
  srcTokenContract: Erc20 | LpTokenUnguarded
  spenderAddress: string
  ownerAddress: string
  spendingValue: BigNumber // max is MaxUint256
  infiniteApproval: boolean
  gasPrice: BigNumber // @dev unused
  callbacks?: {
    onTransactionError?: (error: Error | string) => () => void
  }
  token: Token
}

/**
 * Only used in Deposit LP
 */
export const useCheckAndApproveBatchTokenForTrade = () => {
  const { addTransactions } = useTransactionModal()

  return useCallback(
    async (params: Array<ApproveParam>) => {
      const validParams = params.filter(
        (param) => param.srcTokenContract && param.spendingValue.gte(0),
      )

      const allowances = await Promise.all(
        validParams.map((param) => {
          const { srcTokenContract, spenderAddress, ownerAddress } = param

          return srcTokenContract.allowance(ownerAddress, spenderAddress)
        }),
      )

      const tokensToBeApproved = validParams.filter((param, index) =>
        allowances[index].lt(param.spendingValue),
      )
      const transactions = tokensToBeApproved.map((param) => async () => {
        const {
          srcTokenContract,
          spenderAddress,
          spendingValue,
          infiniteApproval,
          callbacks,
        } = param

        try {
          return await srcTokenContract.approve(
            spenderAddress,
            infiniteApproval ? MaxUint256 : spendingValue,
          )
        } catch (error) {
          callbacks?.onTransactionError?.(error as Error)
          throw error
        }
      })

      if (transactions.length > 0) {
        const summary = tokensToBeApproved.map((param) => ({
          label: "Approve",
          amount: "",
          token: param.token,
        }))
        await addTransactions(transactions, summary, true)
      }
    },
    [addTransactions],
  )
}
